import { Category } from '@/components/base';
import BaseApi, { API_BASE_URL, ApiResponse } from './base';
import { Guide } from './guides';

export interface CourseTranslation {
  id: string;
  language: 'en' | 'es';
  name: string;
  description?: string;
}

export interface CourseGuide {
  id: string;
  guideId: string;
  position: number;
  guide: Guide;
}

export interface CourseEnrollment {
  id: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  enrolledBy: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  enrolledAt: string;
  deadline?: string;
}

export interface CourseDepartmentAssignment {
  id: string;
  department: {
    id: string;
    name: string;
  };
  assignedBy: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  assignedAt: string;
}

export interface Course {
  id: string;
  translations?: CourseTranslation[];
  author: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  isPublished: boolean;
  isRecommended: boolean;
  courseGuides?: CourseGuide[];
  categories?: Category[];
  createdAt: string;
  updatedAt: string;
}

export interface GuideOrder {
  guideId: string;
  position: number;
}

export interface CreateCourseRequest {
  name: string;
  description?: string;
  categoryIds?: string[];
  guideOrders?: GuideOrder[];
  spanishName?: string;
  spanishDescription?: string;
  isRecommended?: boolean;
}

export interface UpdateCourseRequest {
  name?: string;
  description?: string;
  isRecommended?: boolean;
  categoryIds?: string[];
  spanishName?: string;
  spanishDescription?: string;
  guideOrders?: GuideOrder[];
}

export interface EnrollUserRequest {
  deadline?: string;
}

export interface AssignDepartmentRequest {
  deadline?: string;
}

export interface CourseProgress {
  id: string;
  courseId: string;
  isCompleted: boolean;
  completedAt?: string | null;
  startTime?: string;
}

export interface GuideProgressItem {
  guideId: string;
  isCompleted: boolean;
  hasStarted: boolean;
  completedAt: string | null;
}

export interface CourseGuidesProgress {
  guideProgress: GuideProgressItem[];
}

class CoursesApi extends BaseApi {
  constructor(baseURL: string = API_BASE_URL) {
    super(baseURL);
  }

  async getCourses(params?: {
    cursor?: string;
    limit?: number;
    categoryId?: string;
    categoryIds?: string[];
    isPublished?: boolean;
    isRecommended?: boolean;
    language?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    useDraftData?: boolean;
  }): Promise<ApiResponse<Course[]>> {
    const processedParams = { ...params };
    // Convert categoryIds array to comma-separated string for query params
    if (processedParams?.categoryIds && processedParams.categoryIds.length > 0) {
      (processedParams as Record<string, unknown>).categoryIds = processedParams.categoryIds.join(',');
    }
    return this.get<Course[]>(`/courses`, {
      params: processedParams || {},
    });
  }

  async getCourse(id: string): Promise<ApiResponse<Course>> {
    return this.get<Course>(`/courses/${id}`);
  }

  async getPublishedVersion(id: string): Promise<ApiResponse<Course>> {
    return this.get<Course>(`/courses/${id}/published-version`);
  }

  async createCourse(data: CreateCourseRequest): Promise<ApiResponse<Course>> {
    return this.post<Course>('/courses', data);
  }

  async updateCourse(id: string, data: UpdateCourseRequest): Promise<ApiResponse<Course>> {
    return this.put<Course>(`/courses/${id}`, data);
  }

  async deleteCourse(id: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/courses/${id}`);
  }

  async publishCourse(id: string): Promise<ApiResponse<Course>> {
    return this.post<Course>(`/courses/${id}/publish`, {});
  }

  async unpublishCourse(id: string): Promise<ApiResponse<{}>> {
    return this.post<{}>(`/courses/${id}/unpublish`, {});
  }

  async getCourseEnrollments(courseId: string): Promise<ApiResponse<CourseEnrollment[]>> {
    return this.get<CourseEnrollment[]>(`/courses/${courseId}/enrollments`);
  }

  async enrollUser(courseId: string, userId: string, data?: EnrollUserRequest): Promise<ApiResponse<CourseEnrollment>> {
    return this.post<CourseEnrollment>(`/courses/${courseId}/enroll?userId=${userId}`, data || {});
  }

  async unenrollUser(courseId: string, userId: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/courses/${courseId}/enrollments/${userId}`);
  }

  async getDepartmentAssignments(courseId: string): Promise<ApiResponse<CourseDepartmentAssignment[]>> {
    return this.get<CourseDepartmentAssignment[]>(`/courses/${courseId}/department-assignments`);
  }

  async enrollDepartment(
    courseId: string,
    departmentId: string,
    data?: AssignDepartmentRequest,
  ): Promise<ApiResponse<CourseDepartmentAssignment>> {
    return this.post<CourseDepartmentAssignment>(`/courses/${courseId}/enroll/${departmentId}`, data || {});
  }

  async unenrollDepartment(courseId: string, departmentId: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/courses/${courseId}/enroll/${departmentId}`);
  }

  async getCurrentUserEnrollment(courseId: string): Promise<ApiResponse<CourseEnrollment | null>> {
    return this.get<CourseEnrollment | null>(`/courses/${courseId}/enrollment`);
  }

  async getCurrentUserProgress(courseId: string): Promise<ApiResponse<CourseProgress | null>> {
    return this.get<CourseProgress | null>(`/courses/${courseId}/progress`);
  }

  async updateCurrentUserProgress(
    courseId: string,
    data: {
      isCompleted?: boolean;
    },
  ): Promise<ApiResponse<CourseProgress>> {
    return this.patch<CourseProgress>(`/courses/${courseId}/progress`, data);
  }

  async getCourseGuidesProgress(courseId: string): Promise<ApiResponse<CourseGuidesProgress>> {
    return this.get<CourseGuidesProgress>(`/courses/${courseId}/guides-progress`);
  }

  async selfEnroll(courseId: string, data?: EnrollUserRequest): Promise<ApiResponse<CourseEnrollment>> {
    // Self-enroll by calling enroll without userId (backend uses req.user.id)
    return this.post<CourseEnrollment>(`/courses/${courseId}/enroll`, data || {});
  }

  async selfUnenroll(courseId: string): Promise<ApiResponse<{}>> {
    // Self-unenroll by calling delete enroll endpoint (backend uses req.user.id)
    return this.delete<{}>(`/courses/${courseId}/enroll`);
  }

  async getUserResumeCourses(params: { language: string; cursor?: string; limit?: number }): Promise<
    ApiResponse<
      Array<{
        course: Course;
        progress: CourseProgress;
      }>
    >
  > {
    return this.get(`/courses/user/resume`, {
      params: {
        language: params.language,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }

  async getRecommendedCourses(params: {
    language: string;
    cursor?: string;
    limit?: number;
  }): Promise<ApiResponse<Course[]>> {
    return this.get(`/courses/user/recommended`, {
      params: {
        language: params.language,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }

  async getUserAssignedCourses(params: { language: string; cursor?: string; limit?: number }): Promise<
    ApiResponse<
      Array<{
        course: Course;
        enrollment: {
          id: string;
          enrolledAt: string;
          deadline?: string | null;
          enrolledBy: {
            id: string;
            email: string;
            firstName?: string;
            lastName?: string;
          };
        };
      }>
    >
  > {
    return this.get(`/courses/user/assigned`, {
      params: {
        language: params.language,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }
}

export default new CoursesApi();
